import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowLeft, Mail } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
};

const PrivacyPolicyPage = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gdpr/privacy-policy")
      .then(res => setPolicy(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FF7A59]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#FF7A59]" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight">{policy?.title || "Privacy Policy"}</h1>
              <p className="text-slate-400 text-sm mt-1">Last updated: {policy?.last_updated || "N/A"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <motion.div className="max-w-4xl mx-auto px-6 py-8 space-y-6" variants={containerVariants} initial="hidden" animate="show">
        {policy?.sections?.map((section, idx) => (
          <motion.div key={idx} variants={itemVariants}>
            <Card className="bg-white border border-slate-200/80 hover:shadow-md transition-shadow duration-300">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3">{section.heading}</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{section.content}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* DPO Contact */}
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900 border-0 text-white">
            <CardContent className="p-6 flex items-center gap-4">
              <Mail className="w-8 h-8 text-[#FF7A59] flex-shrink-0" />
              <div>
                <h3 className="font-bold">Data Protection Officer</h3>
                <p className="text-sm text-slate-400">
                  For any questions about data processing or to exercise your privacy rights, contact us at{" "}
                  <a href={`mailto:${policy?.dpo_contact || "privacy@odesie.io"}`} className="text-[#FF7A59] hover:underline">
                    {policy?.dpo_contact || "privacy@odesie.io"}
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicyPage;
